import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SlidersHorizontal, Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchBar } from "@/components/SearchBar";
import { ListingCard } from "@/components/ListingCard";
import { StateSelect } from "@/components/StateSelect";
import { SuburbPicker } from "@/components/SuburbPicker";
import { CATEGORIES, catName, ERAS } from "@/lib/constants";
import { api, formatErr } from "@/lib/api";
import { useRegion, useAuth } from "@/context/AuthContext";
import { WaitlistBanner } from "@/components/WaitlistBanner";
import { withAds } from "@/components/AdSlot";

export default function Browse() {
  const [sp, setSp] = useSearchParams();
  const { region, setRegion } = useRegion();
  const [items, setItems] = useState(null);
  const q = sp.get("q") || "";
  const category = sp.get("category") || "";
  const sort = sp.get("sort") || "newest";
  const [suburb, setSuburb] = useState(sp.get("near") || sp.get("suburb") || "");
  const [maxPrice, setMaxPrice] = useState(sp.get("max_price") || "");
  const freeOnly = sp.get("free") === "1";
  const hideSold = sp.get("hide_sold") === "1";
  const radius = sp.get("radius") || "any";
  const near = sp.get("lat") && sp.get("radius");

  const set = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };
  const { user } = useAuth();
  const nav = useNavigate();
  const [savedOk, setSavedOk] = useState(false);
  useEffect(() => setSavedOk(false), [sp, region]);

  const saveSearch = async () => {
    if (!user) return nav(`/login?next=${encodeURIComponent(`/browse?${sp.toString()}`)}`);
    const body = { q, category: category || null, state: region, free_only: freeOnly,
      max_price: sp.get("max_price") ? Number(sp.get("max_price")) : null,
      ...(near ? { near: sp.get("near"), lat: Number(sp.get("lat")), lng: Number(sp.get("lng")), radius_km: Number(sp.get("radius")) } : {}) };
    try {
      await api.post("/my/searches", body);
      setSavedOk(true);
      toast.success("Saved! We'll email you when a new match is listed.");
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => {
    setItems(null);
    const params = { q: q || undefined, category: category || undefined, state: region, sort,
      suburb: sp.get("suburb") || undefined, max_price: sp.get("max_price") || undefined, free_only: freeOnly || undefined, hide_sold: hideSold || undefined, era: category === "antiques" ? sp.get("era") || undefined : undefined,
      ...(near ? { lat: sp.get("lat"), lng: sp.get("lng"), radius_km: sp.get("radius") } : {}) };
    api.get("/listings", { params }).then((r) => setItems(r.data));
  }, [sp, region]); // eslint-disable-line

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="max-w-2xl"><SearchBar key={q} initial={q} testId="browse-search" /></div>
      <div className="mt-6 empty:hidden"><WaitlistBanner region={region} /></div>

      <div className="scrollbar-none mt-6 flex gap-2 overflow-x-auto pb-1">
        <button data-testid="browse-cat-all" onClick={() => set("category", "")} className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${!category ? "bg-[#d4f253] text-[#0f2a33]" : "bg-white/5 text-white hover:bg-white/10"}`}>All</button>
        {CATEGORIES.map((c) => (
          <button key={c.id} data-testid={`browse-cat-${c.id}`} onClick={() => set("category", c.id)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${category === c.id ? "bg-[#d4f253] text-[#0f2a33]" : "bg-white/5 text-white hover:bg-white/10"}`}>{c.name}</button>
        ))}
      </div>

      {category === "antiques" && (
        <div data-testid="browse-era-filters" className="scrollbar-none mt-3 flex gap-2 overflow-x-auto pb-1">
          <span className="shrink-0 self-center text-xs font-semibold uppercase tracking-wider text-slate-400">Era</span>
          {[{ id: "", name: "Any" }, ...ERAS.filter((e) => e.id !== "unknown")].map((e) => (
            <button key={e.id || "any"} data-testid={`browse-era-${e.id || "any"}`} onClick={() => set("era", e.id)} className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${(sp.get("era") || "") === e.id ? "bg-[#d4f253] text-[#0f2a33]" : "bg-white/5 text-white hover:bg-white/10"}`}>{e.name}</button>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl bg-[#173742] p-3">
        <SlidersHorizontal className="ml-2 h-4 w-4 text-slate-400" />
        <StateSelect value={region} onChange={setRegion} testId="browse-state-select" allowAll />
        <form onSubmit={(e) => { e.preventDefault(); const n = new URLSearchParams(sp); if (!sp.get("lat")) { suburb ? n.set("suburb", suburb) : n.delete("suburb"); } maxPrice ? n.set("max_price", maxPrice) : n.delete("max_price"); setSp(n); }} className="flex flex-wrap items-center gap-2">
          <div className="w-52"><SuburbPicker testId="browse-near" value={suburb} state={region} className="h-10 w-full rounded-full bg-white px-4 text-sm text-[#0f2a33] outline-none"
            onChange={(v) => { setSuburb(v); if (sp.get("lat")) { const n = new URLSearchParams(sp); ["lat", "lng", "near"].forEach((k) => n.delete(k)); setSp(n); } }}
            onPick={(o) => { const label = `${o.suburb} ${o.postcode}`; setSuburb(label); const n = new URLSearchParams(sp); n.delete("suburb"); n.set("near", label); n.set("lat", o.lat); n.set("lng", o.lng); if (!n.get("radius")) n.set("radius", "25"); setSp(n); }} /></div>
          <Select value={radius} onValueChange={(v) => set("radius", v === "any" ? "" : v)} disabled={!sp.get("lat")}>
            <SelectTrigger data-testid="browse-radius-select" className="h-10 w-32 rounded-full border-white/15 bg-white/5 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any distance</SelectItem>
              {["10", "25", "50"].map((r) => <SelectItem key={r} value={r} data-testid={`browse-radius-option-${r}`}>Within {r} km</SelectItem>)}
            </SelectContent>
          </Select>
          <input data-testid="browse-maxprice-input" type="number" min="0" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max $" className="h-10 w-28 rounded-full bg-white px-4 text-sm text-[#0f2a33] outline-none" />
          <button data-testid="browse-apply-btn" className="h-10 rounded-full bg-white/10 px-4 text-sm font-semibold hover:bg-white/15">Apply</button>
        </form>
        <button data-testid="browse-free-toggle" onClick={() => set("free", freeOnly ? "" : "1")} className={`h-10 rounded-full px-4 text-sm font-semibold transition-colors ${freeOnly ? "bg-[#d4f253] text-[#0f2a33]" : "bg-white/10 hover:bg-white/15"}`}>Free stuff</button>
        <button data-testid="browse-hide-sold-toggle" onClick={() => set("hide_sold", hideSold ? "" : "1")} className={`h-10 rounded-full px-4 text-sm font-semibold transition-colors ${hideSold ? "bg-[#d4f253] text-[#0f2a33]" : "bg-white/10 hover:bg-white/15"}`}>Hide sold</button>
        <div className="ml-auto">
          <Select value={sort} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger data-testid="browse-sort-select" className="h-10 w-40 rounded-full border-white/15 bg-white/5 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="price_asc">Price: low to high</SelectItem>
              <SelectItem value="price_desc">Price: high to low</SelectItem>
              {near && <SelectItem value="distance" data-testid="browse-sort-distance">Nearest first</SelectItem>}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-end justify-between gap-4">
      <h1 className="font-display text-2xl font-bold sm:text-3xl">
        {q ? `Results for "${q}"` : category ? catName(category) : "All items"}
        {near && <span data-testid="browse-near-label" className="ml-3 text-base font-medium text-[#d4f253]">within {sp.get("radius")} km of {sp.get("near")}</span>}
        {items && <span data-testid="browse-result-count" className="ml-3 text-base font-medium text-slate-400">{items.length} found</span>}
      </h1>
        <button data-testid="browse-save-search-btn" onClick={saveSearch} disabled={savedOk} className="flex items-center gap-2 rounded-full border border-[#d4f253]/40 px-4 py-2 text-sm font-semibold text-[#d4f253] transition-colors hover:bg-[#d4f253]/10 disabled:opacity-70">
          {savedOk ? <><BellRing className="h-4 w-4" />Alert saved</> : <><Bell className="h-4 w-4" />Save search &amp; email me new matches</>}
        </button>
      </div>

      {items === null ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-[4/5] animate-pulse rounded-2xl bg-white/5" />)}
        </div>
      ) : items.length ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
          {withAds(items, (l, i) => <ListingCard key={l.id} l={l} i={i} />)}
        </div>
      ) : (
        <div data-testid="browse-empty-state" className="mt-6 rounded-2xl border border-dashed border-white/15 p-12 text-center text-slate-400">Nothing matches yet. Try widening your search or switch to All Australia.</div>
      )}
    </div>
  );
}
